<template>
    <div class="space-y-6">
        <template
            v-for="(enrollmentInput, enrollmentInputIndex) in enrollmentInputs"
            :key="`${enrollmentInputIndex}waitlistEnrollmentSlot`"
        >
            <waitlist-enrollment-slot
                :show-errors="showErrors"
                :enrollment="enrollmentInput"
                :sequence="enrollmentInputIndex + 1"
                :participant-options="participantOptions"
                :collect-additional-comments="collectAdditionalComments"
                :course-id="courseData.id"
                :waitlist-constraints="waitlistConstraints"
                @update:enrollment="
                    handleUpdateEnrollment(enrollmentInputIndex, $event)
                "
            />
        </template>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const WaitlistEnrollmentCustomerPickerCourseFragment = graphql(`
    fragment WaitlistEnrollmentCustomerPickerCourseFragment on Course {
        id
        savedParticipantsWithWaitlistStatus {
            status
            customer {
                id
                commaName
                firstName
                middleName
                lastName
                dateOfBirth
            }
        }
    }
`)
</script>

<script setup lang="ts">
import { getFragmentData } from '~/resources/graphql'
import { computed } from 'vue'
import WaitlistEnrollmentSlot from '~/components/Waitlist/WaitlistEnrollmentSlot.vue'
import type {
    WaitlistEnrollmentInput,
    WaitlistEnrollmentInputArray,
    WaitlistEnrollmentInputExistingParticipant
} from '~/composables/wait-list-enrollment-input'
import type { WaitlistedCustomerConstraints } from '~/components/Waitlist/waitlisted-customer-constraint'
import type { FragmentType } from '@/generated/gql'

const props = defineProps<{
    enrollmentInputs: WaitlistEnrollmentInputArray
    collectAdditionalComments: boolean
    showErrors: boolean
    waitlistConstraints: WaitlistedCustomerConstraints
    course: FragmentType<typeof WaitlistEnrollmentCustomerPickerCourseFragment>
}>()

const emit = defineEmits<{
    (e: 'update:enrollmentInputs', payload: WaitlistEnrollmentInputArray): void
}>()

const courseData = computed(() =>
    getFragmentData(
        WaitlistEnrollmentCustomerPickerCourseFragment,
        props.course
    )
)

const participantOptions = computed<
    (WaitlistEnrollmentInputExistingParticipant & {
        disabled: boolean
    })[]
>(() => {
    return courseData.value.savedParticipantsWithWaitlistStatus.map(
        participant => {
            return {
                type: 'existing',
                id: participant.customer.id,
                commaName: participant.customer.commaName,
                firstName: participant.customer.firstName,
                middleName: participant.customer.middleName,
                lastName: participant.customer.lastName,
                dateOfBirth: participant.customer.dateOfBirth ?? '',
                alreadyOnWaitlist: participant.status !== null,
                disabled: selectedCustomers.value.has(participant.customer.id)
            }
        }
    )
})

// Currently selected customers
const selectedCustomers = computed(() => {
    const set = new Set<string>()
    props.enrollmentInputs.forEach(value => {
        if (value.data !== null && value.data.type === 'existing') {
            set.add(value.data.id)
        }
    })
    return set
})

function handleUpdateEnrollment(
    enrollmentInputIndex: number,
    enrollment: WaitlistEnrollmentInput
) {
    const oldEnrollments = props.enrollmentInputs
    const enrollments = [
        ...oldEnrollments.slice(0, enrollmentInputIndex),
        enrollment,
        ...oldEnrollments.slice(enrollmentInputIndex + 1)
    ]

    emit('update:enrollmentInputs', enrollments)
}
</script>
